import { useMemo } from 'react'

import { X } from 'lucide-react'
import type { FC } from 'react'

import { Button, buttonVariants } from '@/components/ui/button'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import type {
	SelectFilterDefinition,
	TextFilterDefinition,
} from '../TableFilters'
import { FilterList } from './FilterList'
import { OpenDialogButton } from './OpenDialogButton'
import type { CustomFilterShape } from './useFilters'
import { useFilters } from './useFilters'

interface Props {
	options: (SelectFilterDefinition | TextFilterDefinition)[]
	setTableFilters: (filters: CustomFilterShape) => void
	value: CustomFilterShape
}

const CustomFilter: FC<Props> = ({ setTableFilters, options, value }) => {
	const {
		filters,
		resetFilters,
		closeAndRemoveEmpty,
		open,
		toggleOpen,
		onFilterChange,
	} = useFilters(value, setTableFilters)

	const filteredByColumns = useMemo(
		() =>
			options
				.map(({ column }) => column)
				.reduce(
					(acc, curr) => ({ ...acc, [curr.value]: curr.label }),
					{}
				),
		[options]
	)

	return (
		<Popover open={open} onOpenChange={toggleOpen}>
			<div
				className={cn(
					buttonVariants({ variant: 'ghost', size: 'sm' }),
					`${filters.length > 0 ? 'bg-green-200 dark:bg-green-600 hover:bg-green-100 pr-0' : ''}`,
					'flex items-center gap-2'
				)}
			>
				<PopoverTrigger aria-label="Open filter dialog">
					<OpenDialogButton
						filters={value}
						columns={filteredByColumns}
					/>
				</PopoverTrigger>

				{filters.length > 0 && (
					<Button
						variant="ghost"
						size="sm"
						onClick={resetFilters}
						aria-label="remove applied filters"
						className="hover:bg-green-100"
					>
						<X className="h-4 w-4" />
					</Button>
				)}
			</div>
			<PopoverContent className="w-[90vw] md:min-w-152 md:w-auto max-w-lg">
				<FilterList
					open={open}
					closeAndRemoveEmpty={closeAndRemoveEmpty}
					filters={filters}
					options={options}
					onFilterChange={onFilterChange}
				/>
			</PopoverContent>
		</Popover>
	)
}

export default CustomFilter
export { CustomFilterShape }
